import { useCallback, useState } from 'react'
import { ErrorBanner } from '../../components/Async'
import { ApiError, api, type ObservedWindow } from '../../lib/api'

/**
 * The prompt an observation carried, fetched only when somebody asks for it.
 *
 * It is not on this page already, and that is the design rather than an oversight. The prompt is not
 * in the OCSF record — so it is not in a query result, not in a saved query, and not in any SIEM
 * export — and it is read from the byte-exact body the device sent, at the moment an analyst opens it.
 * That is the same argument ADR-S-025 makes for a screen capture staying on the device: the sensitive
 * thing is exposed when a human had a reason, which is a property that survives being read out loud.
 *
 * Folded shut for the same reason. Somebody reading an alert should see the rule that matched without
 * also seeing what a colleague typed, unless they decide they need to.
 */
export function PromptPanel({ eventRecordId }: { eventRecordId: string }) {
  const [windows, setWindows] = useState<ObservedWindow[] | null>(null)
  const [failure, setFailure] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const show = useCallback(async () => {
    setBusy(true)
    setFailure(null)
    try {
      setWindows((await api.getEventPrompt(eventRecordId)).windows)
    } catch (error) {
      setFailure(error instanceof ApiError ? error.message : 'The prompt could not be read.')
    } finally {
      setBusy(false)
    }
  }, [eventRecordId])

  return (
    <div className="card">
      <div className="card-body">
        <h3 style={{ marginTop: 0 }}>Prompt</h3>

        <ErrorBanner message={failure} />

        {windows === null ? (
          <>
            <button type="button" onClick={show} disabled={busy} data-testid="show-prompt">
              {busy ? 'Reading…' : 'Show the prompt'}
            </button>
            <div className="hint">
              Read from the body this device sent, which the server keeps byte-exact. It is not part of
              the event record, so it never appears in a query result or in a SIEM export — the only
              way to see it is to ask here.
            </div>
          </>
        ) : windows.length === 0 ? (
          <div className="empty" data-testid="no-prompt">
            This event carries no observed prompt.
            <div className="hint">
              Only a published prompt observation carries one. A window inventory, a screen capture or a
              log entry has no prompt to show, and neither does an observation from a policy that does
              not publish its result.
            </div>
          </div>
        ) : (
          <div data-testid="prompt-windows">
            {windows.map((window, index) => (
              <div key={index} className="subsection">
                <div className="muted">
                  {window.windowTitle ?? 'an untitled window'}
                  {window.processName && ` · ${window.processName}`}
                  {window.status && ` · ${window.status}`}
                  {window.extractionMethod && ` · read by ${window.extractionMethod}`}
                </div>

                {window.promptText ? (
                  <pre className="prompt-text" data-testid="prompt-text">
                    {window.promptText}
                  </pre>
                ) : (
                  <div className="hint">
                    No text was captured from this window. The policy may redact it, or the control may
                    have been empty when the agent looked.
                  </div>
                )}

                {window.matches.length > 0 && (
                  <table data-testid="prompt-matches">
                    <thead>
                      <tr>
                        <th>Rule</th>
                        <th>Term</th>
                        <th className="secondary">Where it appeared</th>
                      </tr>
                    </thead>
                    <tbody>
                      {window.matches.map((match, position) => (
                        <tr key={position}>
                          <td>{match.ruleName ?? '—'}</td>
                          <td className="mono">{match.term ?? '—'}</td>
                          <td className="muted secondary">{match.context ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
